import { z } from "zod";

import { zDate } from "@/lib/zod/show-fragments";
import { zPlayerRef, zTeamRef } from "@/pages/sports/schemas/sports-fragments";
import type {
  ILeaderRow,
  ILeagueStandings,
  IStandingsGroup,
  IStandingsRow,
} from "@/types/league-standings";

const zRow: z.ZodType<IStandingsRow> = z
  .object({
    rank: z.number().nullish(),
    team: zTeamRef.nullish(),
    played: z.number().nullish(),
    won: z.number().nullish(),
    drawn: z.number().nullish(),
    lost: z.number().nullish(),
    scoredFor: z.number().nullish(),
    scoredAgainst: z.number().nullish(),
    diff: z.number().nullish(),
    points: z.number().nullish(),
    winPct: z.number().nullish(),
    form: z.array(z.string()).nullish(),
  })
  .passthrough();

const zGroup: z.ZodType<IStandingsGroup> = z
  .object({
    name: z.string().nullish(),
    phase: z.string().nullish(),
    rows: z.array(zRow).nullish(),
  })
  .passthrough();

const zLeaderRow: z.ZodType<ILeaderRow> = z
  .object({
    rank: z.number().nullish(),
    player: zPlayerRef.nullish(),
    team: zTeamRef.nullish(),
    value: z.number().nullish(),
    gamesPlayed: z.number().nullish(),
  })
  .passthrough();

/**
 * Read-only view schema for `GET /api/leagues/{id}/standings` — the whole
 * LeagueStandingsDbDto (one season table, league stats and leaders). Every enum
 * (`phase`, form letters) is a plain string so a new value still renders.
 */
export const leagueStandingsViewSchema: z.ZodType<ILeagueStandings> = z
  .object({
    season: z.string().nullish(),
    source: z.string().nullish(),
    groups: z.array(zGroup).nullish(),
    stats: z
      .object({
        matchesPlayed: z.number().nullish(),
        goalsPerMatch: z.number().nullish(),
        homeWinPct: z.number().nullish(),
        awayWinPct: z.number().nullish(),
        drawPct: z.number().nullish(),
      })
      .passthrough()
      .nullish(),
    leaders: z
      .object({
        goals: z.array(zLeaderRow).nullish(),
        assists: z.array(zLeaderRow).nullish(),
      })
      .passthrough()
      .nullish(),
    updatedAt: zDate,
    updatedBy: z.string().nullish(),
  })
  .passthrough();
